import { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Phone, Mail, MapPin, Instagram, Check, Loader2, ArrowRight, ArrowUpRight } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { trackWhatsAppClick, trackCallClick, trackEmailClick } from "@/lib/analytics";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const PHONE_DISPLAY = "+34 644 100 551";
const PHONE_TEL = "+34644100551";
const EMAIL = "sercom.construction@gmail.com";
const WHATSAPP_MSG =
  "Hola, os escribo a través de la web de Sercom. Me gustaría recibir más información sobre vuestras viviendas.";
const WHATSAPP_URL = `https://wa.me/34644100551?text=${encodeURIComponent(WHATSAPP_MSG)}`;
const EMAIL_HREF = `mailto:${EMAIL}?subject=${encodeURIComponent("Consulta desde la web")}`;

type Canal = "whatsapp" | "llamada" | "email";

const canales: { id: Canal; label: string }[] = [
  { id: "whatsapp", label: "WhatsApp" },
  { id: "llamada", label: "Llamada" },
  { id: "email", label: "Email" },
];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};

export default function Contacto() {
  const [form, setForm] = useState({ nombre: "", telefono: "", email: "", mensaje: "" });
  const [canal, setCanal] = useState<Canal>("whatsapp");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    const { error: insertError } = await supabase.from("contact_requests").insert({
      nombre: form.nombre.trim(),
      telefono: form.telefono.trim() || null,
      email: form.email.trim() || null,
      mensaje: form.mensaje.trim() || null,
      canal,
      origen: typeof document !== "undefined" ? document.referrer || "directo" : null,
    });
    setSending(false);
    if (insertError) {
      setError("No hemos podido enviar tu mensaje. Escríbenos por WhatsApp o inténtalo de nuevo.");
      return;
    }
    setSent(true);
    setForm({ nombre: "", telefono: "", email: "", mensaje: "" });
  };

  const inputClass =
    "rounded-xl border-border/70 bg-card/60 px-4 py-5 text-sm placeholder:text-muted-foreground/70 focus-visible:ring-accent/40";

  return (
    <Layout>
      <Helmet>
        <title>Contacto | Sercom</title>
        <meta
          name="description"
          content="Habla con Sercom: WhatsApp, teléfono o email. También puedes dejarnos tus datos y te contactamos nosotros."
        />
        <link rel="canonical" href="https://sercomcs.com/Contacto" />
        <meta property="og:title" content="Contacto | Sercom" />
        <meta property="og:description" content="Habla con Sercom o déjanos tus datos y te contactamos." />
        <meta property="og:url" content="https://sercomcs.com/Contacto" />
      </Helmet>

      <div className="pt-32 pb-24 md:pt-36">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Cabecera */}
          <motion.div {...fadeUp} transition={{ duration: 0.5, ease: "easeOut" }} className="max-w-2xl">
            <span className="text-accent text-xs font-semibold tracking-[0.3em] uppercase">Contacto</span>
            <h1 className="mt-3 text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
              Hablemos de tu <span className="text-gradient-gold">próximo hogar</span>
            </h1>
            <p className="mt-4 text-base md:text-lg text-muted-foreground">
              Elige el canal que prefieras o déjanos tus datos y te contactamos nosotros.
            </p>
          </motion.div>

          <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
            {/* Canales directos */}
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}>
              <div className="divide-y divide-border/60 border-y border-border/60">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick()}
                  className="group flex items-center gap-5 py-5 transition-colors"
                >
                  <WhatsAppIcon className="h-10 w-10 shrink-0 transition-transform duration-300 group-hover:scale-110" />
                  <span className="min-w-0">
                    <span className="block text-base font-semibold">WhatsApp</span>
                    <span className="block text-sm text-muted-foreground">{PHONE_DISPLAY}</span>
                  </span>
                  <ArrowUpRight className="ml-auto h-5 w-5 text-muted-foreground transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
                </a>

                <a
                  href={`tel:${PHONE_TEL}`}
                  onClick={() => trackCallClick()}
                  className="group flex items-center gap-5 py-5 transition-colors"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-accent/30 text-accent transition-all duration-300 group-hover:bg-accent group-hover:text-accent-foreground">
                    <Phone className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-base font-semibold">Llamar</span>
                    <span className="block text-sm text-muted-foreground">{PHONE_DISPLAY}</span>
                  </span>
                  <ArrowUpRight className="ml-auto h-5 w-5 text-muted-foreground transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
                </a>

                <a
                  href={EMAIL_HREF}
                  onClick={() => trackEmailClick()}
                  className="group flex items-center gap-5 py-5 transition-colors"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-accent/30 text-accent transition-all duration-300 group-hover:bg-accent group-hover:text-accent-foreground">
                    <Mail className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-base font-semibold">Email</span>
                    <span className="block truncate text-sm text-muted-foreground">{EMAIL}</span>
                  </span>
                  <ArrowUpRight className="ml-auto h-5 w-5 text-muted-foreground transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
                </a>
              </div>

              <div className="mt-8 flex flex-col gap-3 text-sm text-muted-foreground">
                <p className="flex items-center gap-2.5">
                  <MapPin className="h-4 w-4 text-accent" />
                  Vallès Occidental, Barcelona
                </p>
                <a
                  href="https://www.instagram.com/sercom.cs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 transition-colors hover:text-accent"
                >
                  <Instagram className="h-4 w-4 text-accent" />
                  @sercom.cs
                </a>
              </div>
            </motion.div>

            {/* Formulario */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
              className="glass-surface apple-shadow rounded-2xl p-6 md:p-8"
            >
              {sent ? (
                <div className="flex min-h-[320px] flex-col items-center justify-center text-center">
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 16 }}
                    className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-accent"
                  >
                    <Check className="h-7 w-7" />
                  </motion.span>
                  <h2 className="text-xl font-bold">Mensaje recibido</h2>
                  <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                    Gracias. Te contactamos por {canales.find((c) => c.id === canal)?.label.toLowerCase()} lo antes
                    posible.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-6 text-sm font-medium text-accent hover:underline"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-bold tracking-tight">Déjanos tus datos</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Te contactamos nosotros, sin compromiso.</p>

                  <form onSubmit={handleSubmit} className="mt-6 space-y-3">
                    <Input
                      aria-label="Tu nombre"
                      placeholder="Tu nombre"
                      required
                      maxLength={120}
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      className={inputClass}
                    />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input
                        aria-label="Teléfono"
                        placeholder="Teléfono"
                        type="tel"
                        maxLength={40}
                        value={form.telefono}
                        onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                        className={inputClass}
                      />
                      <Input
                        aria-label="Email"
                        placeholder="Email"
                        type="email"
                        maxLength={160}
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <Textarea
                      aria-label="Mensaje"
                      placeholder="¿Qué estás buscando?"
                      rows={4}
                      maxLength={2000}
                      value={form.mensaje}
                      onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                      className="rounded-xl border-border/70 bg-card/60 px-4 py-3 text-sm placeholder:text-muted-foreground/70 focus-visible:ring-accent/40"
                    />

                    <fieldset className="pt-1">
                      <legend className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Cómo prefieres que te contactemos
                      </legend>
                      <div className="flex flex-wrap gap-2">
                        {canales.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setCanal(c.id)}
                            aria-pressed={canal === c.id}
                            className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                              canal === c.id
                                ? "border-accent bg-accent/10 text-accent"
                                : "border-border/70 text-muted-foreground hover:border-accent/40 hover:text-foreground"
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </fieldset>

                    {error && <p className="text-sm text-destructive">{error}</p>}

                    <Button
                      type="submit"
                      disabled={sending}
                      className="mt-2 w-full gap-2 rounded-xl py-6 text-base font-semibold transition-all hover:-translate-y-0.5"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                      {sending ? "Enviando..." : "Enviar"}
                    </Button>
                    <p className="pt-1 text-center text-[11px] text-muted-foreground">
                      Respuesta habitual en menos de 1 hora.
                    </p>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
